import {
  savePushSubscription,
  deletePushSubscription
} from './subscriptions';
import type { PushPermissionState } from './types';

/**
 * Cliente Web Push del navegador. Usa la API estándar (serviceWorker + PushManager).
 *
 * Requisitos del entorno:
 *   - HTTPS (o localhost).
 *   - Service worker registrado y activo.
 *   - VITE_VAPID_PUBLIC_KEY configurado.
 *   - Permiso del usuario concedido.
 *
 * iOS: requiere iOS 16.4+ y la PWA instalada en la pantalla de inicio.
 */

function isSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  return true;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

function getVapidPublicKey(): string | null {
  const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  return typeof key === 'string' && key.length > 0 ? key : null;
}

export function getPermissionState(): PushPermissionState {
  if (!isSupported()) return 'unsupported';
  const perm = Notification.permission;
  if (perm === 'granted') return 'granted';
  if (perm === 'denied') return 'denied';
  return 'default';
}

export interface SubscribeResult {
  ok: boolean;
  reason?:
    | 'unsupported'
    | 'no_vapid_key'
    | 'no_service_worker'
    | 'permission_denied'
    | 'subscribe_failed'
    | 'save_failed';
}

export interface SubscribeOptions {
  reminderHour: number;
  timezoneOffsetMinutes: number;
}

/**
 * Pide permiso, se suscribe al PushManager y guarda la suscripción en Supabase.
 * Idempotente: si ya existe una suscripción activa, la reemplaza.
 */
export async function subscribeToPush(
  options: SubscribeOptions
): Promise<SubscribeResult> {
  if (!isSupported()) return { ok: false, reason: 'unsupported' };

  const vapid = getVapidPublicKey();
  if (!vapid) return { ok: false, reason: 'no_vapid_key' };

  if (!navigator.serviceWorker.controller && !navigator.serviceWorker.ready) {
    return { ok: false, reason: 'no_service_worker' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_denied' };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return { ok: false, reason: 'no_service_worker' };
  }

  let sub: PushSubscription;
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      sub = existing;
    } else {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid)
      });
    }
  } catch {
    return { ok: false, reason: 'subscribe_failed' };
  }

  const json = sub.toJSON();
  const endpoint = json.endpoint ?? sub.endpoint;
  const keys = json.keys ?? {};
  const p256dh = keys.p256dh ?? '';
  const auth = keys.auth ?? '';

  if (!endpoint || !p256dh || !auth) {
    return { ok: false, reason: 'subscribe_failed' };
  }

  const saved = await savePushSubscription({
    endpoint,
    p256dh,
    auth,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    reminderHour: options.reminderHour,
    timezoneOffsetMinutes: options.timezoneOffsetMinutes
  });

  if (!saved) return { ok: false, reason: 'save_failed' };
  return { ok: true };
}

/**
 * Cancela la suscripción en el navegador y la borra de Supabase.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isSupported()) return false;

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return false;
  }

  const sub = await registration.pushManager.getSubscription();
  if (!sub) return true;

  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // Continúa: aunque falle en el navegador, limpiamos en Supabase.
  }

  await deletePushSubscription(endpoint);
  return true;
}

/**
 * True si este navegador ya tiene una suscripción push activa.
 */
export async function isSubscribed(): Promise<boolean> {
  if (!isSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}